import React, { useState } from 'react';
import { TagIcon } from '@/src/components/icons';
import { Card, CardContent, CardHeader } from '@/src/components/ui/card';
import { Button } from '@/src/components/ui/button';
import { Badge } from '@/src/components/ui/badge';
import { Input } from '@/src/components/ui/input';
import PropertyCard from './PropertyCard';

const CollectionsView = ({ 
  properties, 
  onPropertyClick, 
  toggleFavorite, 
  isFavorite, 
  onBack, 
  getAllTags,
  getPropertyTags,
  onOpenTagManager,
  updateTag,
  deleteTag,
  hasNote
}) => {
  const allTags = getAllTags();
  const [expandedTag, setExpandedTag] = useState(null);
  const [editingTag, setEditingTag] = useState(null);
  const [editTagName, setEditTagName] = useState('');
  const [editTagColor, setEditTagColor] = useState('');
  const [refresh, setRefresh] = useState(0);

  const colors = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', 
    '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'
  ];

  const handleEditTag = (tag, e) => {
    e.stopPropagation();
    setEditingTag(tag.id);
    setEditTagName(tag.name);
    setEditTagColor(tag.color);
  };

  const handleSaveEdit = (e) => {
    e.stopPropagation();
    if (editTagName.trim() && editingTag) {
      updateTag(editingTag, { name: editTagName.trim(), color: editTagColor });
      setEditingTag(null);
      setEditTagName('');
      setEditTagColor('');
      setRefresh(r => r + 1);
    }
  };

  const handleCancelEdit = (e) => {
    e.stopPropagation();
    setEditingTag(null);
    setEditTagName('');
    setEditTagColor('');
  };

  const handleDeleteTag = (tagId, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this tag? It will be removed from all properties.')) {
      deleteTag(tagId);
      setRefresh(r => r + 1);
    }
  };

  if (allTags.length === 0) {
    return (
      <div className="min-h-screen p-4">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <Button onClick={onBack} variant="ghost" size="sm" className="mb-4">
              ← Back to Search
            </Button>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Collections</h1>
            <p className="text-gray-600">Organize your properties with tags and collections</p>
          </div>
          
          <div className="text-center py-12">
            <div className="w-16 h-16 text-gray-300 mx-auto mb-4">
              <TagIcon />
            </div>
            <h3 className="text-xl font-medium text-gray-900 mb-2">No Collections Yet</h3>
            <p className="text-gray-500">
              Start creating tags for your properties to organize them into collections.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <Button onClick={onBack} variant="ghost" size="sm" className="mb-4">
            ← Back to Search
          </Button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Collections</h1>
          <p className="text-gray-600">Properties organized by tags</p>
        </div>

        <div className="space-y-6">
          {allTags.map((tag) => {
            const tagPropertyCount = tag.properties ? tag.properties.length : 0;
            const isExpanded = expandedTag === tag.id;
            const tagProperties = isExpanded ? properties.filter(property =>
              getPropertyTags(property.roll_number).some(pTag => pTag.id === tag.id)
            ) : [];

            return (
              <Card key={tag.id}>
                <CardHeader 
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedTag(isExpanded ? null : tag.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      {editingTag === tag.id ? (
                        <>
                          <Input
                            value={editTagName}
                            onChange={(e) => {
                              e.stopPropagation();
                              setEditTagName(e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-40"
                            size="sm"
                          />
                          <div className="flex gap-1">
                            {colors.map(color => (
                              <button
                                key={color}
                                className={`w-5 h-5 rounded-full border ${editTagColor === color ? 'border-gray-900' : 'border-gray-300'}`}
                                style={{ backgroundColor: color }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditTagColor(color);
                                }}
                              />
                            ))}
                          </div>
                          <button
                            onClick={handleSaveEdit}
                            className="text-green-600 hover:text-green-700 text-xs px-2 py-1 border rounded"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="text-gray-600 hover:text-gray-700 text-xs px-2 py-1 border rounded"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <Badge 
                            style={{ backgroundColor: tag.color, color: '#ffffff', border: 'none' }}
                            className="text-sm"
                          >
                            {tag.name}
                          </Badge>
                          <span className="text-sm text-gray-500">
                            {tagPropertyCount} {tagPropertyCount === 1 ? 'property' : 'properties'}
                          </span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {editingTag !== tag.id && (
                        <>
                          <button
                            onClick={(e) => handleEditTag(tag, e)}
                            className="text-blue-600 hover:text-blue-700 text-xs px-2 py-1"
                          >
                            Edit
                          </button>
                          <button
                            onClick={(e) => handleDeleteTag(tag.id, e)}
                            className="text-red-600 hover:text-red-700 text-xs px-2 py-1"
                          >
                            Delete
                          </button>
                        </>
                      )}
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="20" 
                        height="20" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                        className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      >
                        <path d="m6 9 6 6 6-6"></path>
                      </svg>
                    </div>
                  </div>
                </CardHeader>
                
                {isExpanded && (
                  <CardContent>
                    {tagProperties.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">No properties in this collection</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {tagProperties.map((property) => (
                          <PropertyCard
                            key={property.roll_number}
                            property={property}
                            onPropertyClick={onPropertyClick}
                            toggleFavorite={toggleFavorite}
                            isFavorite={isFavorite}
                            getPropertyTags={getPropertyTags}
                            onOpenTagManager={onOpenTagManager}
                            hasNote={hasNote}
                          />
                        ))}
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CollectionsView;